import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface ImportableAudiobook {
  id: string;
  name: string;
  author: string;
}

interface AudiobookImportDialogProps {
  open: boolean;
  onClose: () => void;
}

const AudiobookImportDialog: React.FC<AudiobookImportDialogProps> = observer(({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    audiobookStore: {
      importableAudiobooks,
      getImportableAudiobooks,
      getSelectedAudiobook,
      selectedImportedAudiobook,
      importLoading,
    },
  } = useMst();
  const [searchTerm, setSearchTerm] = useState('');
  const [audiobooks, setAudiobooks] = useState<ImportableAudiobook[]>([]);

  useEffect(() => {
    if (open) {
      getImportableAudiobooks();
    }
  }, [open, getImportableAudiobooks]);

  useEffect(() => {
    setAudiobooks(importableAudiobooks);
  }, [importableAudiobooks]);

  const handleSelectAudiobook = async (id: string) => {
    await getSelectedAudiobook(id, true);
    onClose();
  };

  useEffect(() => {
    if (selectedImportedAudiobook) {
      navigate('/audiobooks/create');
    }
  }, [selectedImportedAudiobook, navigate]);

  const filteredAudiobooks = audiobooks
    .filter((audiobook: ImportableAudiobook) => audiobook.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('import_audiobook')}</DialogTitle>
      <DialogContent>
        <TextField
          label={t('search')}
          variant="outlined"
          fullWidth
          margin="normal"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {importLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {filteredAudiobooks.map((audiobook: ImportableAudiobook, index: number) => (
              <ListItem button key={`${audiobook.id}-${index}`} onClick={() => handleSelectAudiobook(audiobook.id)}>
                <ListItemText primary={audiobook.name} secondary={audiobook.author} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default AudiobookImportDialog;
